import { useEffect, useState } from "react";
import api, { fmtDate, formatErr } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Pencil, Upload, Mail, KeyRound, Search, X } from "lucide-react";
import BulkUsersDialog from "../components/BulkUsersDialog";
import { isAdminRole, isSuperAdmin, isCampusAdmin } from "../lib/roles";
import { campusLabel } from "../lib/campus";

const EMPTY_FORM = { name: "", email: "", password: "", role: "staff", department: "", campus: "", supervisor_name: "", supervisor: "", employee_id: "", phone: "", unit_scope: "" };
const EMPTY_EDIT = { name: "", email: "", role: "staff", department: "", campus: "", supervisor_name: "", supervisor: "", employee_id: "", phone: "", password: "", unit_scope: "" };

/* Free-text field with dropdown suggestions from a string list */
function SuggestField({ value, onChange, suggestions, placeholder, className = "", required = false, testid }) {
  const [show, setShow] = useState(false);
  const results = suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()) && s !== value).slice(0, 8);
  return (
    <div className="relative">
      <Input
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setShow(true); }}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        className={`mt-2 rounded-none ${className}`}
        autoComplete="off"
        data-testid={testid}
      />
      {show && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 bg-white border border-slate-200 shadow-md mt-1 max-h-40 overflow-y-auto">
          {results.map((s) => (
            <button key={s} type="button"
              className="w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              onMouseDown={(e) => { e.preventDefault(); onChange(s); setShow(false); }}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Supervisor name field — shows users, also fills email on select */
function SupervisorNameField({ value, onChangeName, onChangeEmail, users }) {
  const [show, setShow] = useState(false);
  const results = value ? users.filter((u) => u.name.toLowerCase().includes(value.toLowerCase())).slice(0, 8) : [];
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChangeName(e.target.value); setShow(true); }}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        placeholder="Search by name…"
        className="mt-2 rounded-none"
        autoComplete="off"
      />
      {show && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 bg-white border border-slate-200 shadow-md mt-1 max-h-40 overflow-y-auto">
          {results.map((u) => (
            <button key={u.id} type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0"
              onMouseDown={(e) => { e.preventDefault(); onChangeName(u.name); onChangeEmail(u.email); setShow(false); }}>
              <div className="font-medium text-slate-900">{u.name}</div>
              <div className="text-xs text-slate-500">{u.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── FormFields lifted out of UsersPage so React never remounts it on re-render ── */
function FormFields({ values, set, isEdit, t, allNames, allDepts, users, canSuper, limited = false, fixedCampus = "" }) {
  return (
    <div className="space-y-4">
      <div><Label className="label-mono">{t("full_name")}</Label>
        <SuggestField required value={values.name} onChange={(v) => set({ ...values, name: v })}
          suggestions={allNames} testid="user-name-input" /></div>

      <div><Label className="label-mono">{t("us_phone")}</Label>
        <Input value={values.phone || ""} onChange={(e) => set({ ...values, phone: e.target.value })} placeholder="0812xxxxxxxx" className="mt-2 rounded-none" data-testid="user-phone-input" />
        <p className="text-xs text-slate-400 mt-1">{t("us_phone_hint")}</p></div>

      <div><Label className="label-mono">{t("col_employee_id")}</Label>
        <Input value={values.employee_id} onChange={(e) => set({ ...values, employee_id: e.target.value })}
          placeholder="e.g. 0000910439" className="mt-2 rounded-none font-mono" /></div>

      <div><Label className="label-mono">{t("email")}</Label>
        <Input type="email" required placeholder="user@fmi.com" value={values.email}
          onChange={(e) => set({ ...values, email: e.target.value })}
          className="mt-2 rounded-none" data-testid="user-email-input" /></div>

      {!isEdit && (
        <div><Label className="label-mono">{t("password")}</Label>
          <Input type="password" required minLength={6} value={values.password}
            onChange={(e) => set({ ...values, password: e.target.value })}
            className="mt-2 rounded-none" data-testid="user-password-input" /></div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="label-mono">{t("role")}</Label>
          <Select value={values.role} onValueChange={(v) => set({ ...values, role: v })}
            disabled={values.role === "super_admin" && !canSuper}>
            <SelectTrigger className="mt-2 rounded-none" data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="staff">{t("staff")}</SelectItem>
              {!limited && <SelectItem value="admin">{t("admin")}</SelectItem>}
              {!limited && <SelectItem value="campus_admin">{t("campus_admin")}</SelectItem>}
              <SelectItem value="ict_admin">{t("ict_admin")}</SelectItem>
              {!limited && <SelectItem value="leader">{t("leader")}</SelectItem>}
              {(canSuper || values.role === "super_admin") && <SelectItem value="super_admin">{t("super_admin")}</SelectItem>}
            </SelectContent>
          </Select></div>
        <div><Label className="label-mono">{t("department")}</Label>
          <SuggestField value={values.department} onChange={(v) => set({ ...values, department: v })}
            suggestions={allDepts} /></div>
      </div>

      {values.role === "admin" && !limited && (
        <div data-testid="unit-scope-field"><Label className="label-mono">{t("unit_scope")}</Label>
          <div className="mt-2 flex flex-wrap gap-4">
            {["paud", "sd", "smp"].map((code) => {
              const on = (values.unit_scope || "").split(",").includes(code);
              return (
                <label key={code} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-slate-900" checked={on} data-testid={`unit-scope-${code}`}
                    onChange={() => {
                      const cur = (values.unit_scope || "").split(",").filter(Boolean);
                      const next = on ? cur.filter((x) => x !== code) : [...cur, code];
                      set({ ...values, unit_scope: next.join(",") });
                    }} />
                  {code.toUpperCase()}
                </label>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-slate-400">{t("unit_scope_hint")}</p>
        </div>
      )}

      <div><Label className="label-mono">{t("col_campus")}</Label>
        <Select value={fixedCampus || values.campus} disabled={!!fixedCampus} onValueChange={(v) => set({ ...values, campus: v })}>
          <SelectTrigger className="mt-2 rounded-none"><SelectValue placeholder={t("select_campus")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="YPJ Kuala Kencana">YPJ Kuala Kencana</SelectItem>
            <SelectItem value="YPJ TPRA">{campusLabel("YPJ TPRA")}</SelectItem>
          </SelectContent>
        </Select></div>

      <div><Label className="label-mono">{t("supervisor_name")}</Label>
        <SupervisorNameField
          value={values.supervisor_name}
          onChangeName={(v) => set({ ...values, supervisor_name: v })}
          onChangeEmail={(v) => set({ ...values, supervisor: v })}
          users={users}
        /></div>

      <div><Label className="label-mono">{t("supervisor_email")}</Label>
        <Input type="email" value={values.supervisor}
          onChange={(e) => set({ ...values, supervisor: e.target.value })}
          placeholder="@fmi.com" className="mt-2 rounded-none" /></div>

      {isEdit && (
        <div><Label className="label-mono">{t("new_password_optional")}</Label>
          <Input type="password" minLength={6} value={values.password}
            onChange={(e) => set({ ...values, password: e.target.value })}
            placeholder={t("leave_blank_unchanged")} className="mt-2 rounded-none" /></div>
      )}
    </div>
  );
}

export default function UsersPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const limited = isCampusAdmin(user?.role);   // Campus Admin: own campus, staff / ICT Admin accounts only
  const ownCampus = limited ? (user?.campus || "") : "";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(limited ? r.data.filter((u) => u.campus === ownCampus) : r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", limited ? { ...form, campus: ownCampus } : form);
      toast.success(t("user_created_ok"));
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name, email: u.email, role: u.role,
      department: u.department || "", campus: u.campus || "",
      supervisor_name: u.supervisor_name || "",
      supervisor: u.supervisor || "",
      employee_id: u.employee_id || "",
      phone: u.phone || "",
      unit_scope: u.unit_scope || "",
      password: "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    const payload = limited ? { ...editForm, campus: ownCampus } : { ...editForm };
    if (!payload.password) delete payload.password;
    try {
      await api.put(`/users/${editTarget.id}`, payload);
      toast.success(t("user_updated_ok"));
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const sendLink = async (u, kind) => {
    if (!window.confirm(t(kind === "invite" ? "invite_confirm" : "reset_confirm").replace("{name}", u.name).replace("{email}", u.email))) return;
    try {
      const r = await api.post(`/users/${u.id}/${kind === "invite" ? "invite" : "reset-password"}`);
      if (r.data.sent) { toast.success(t(kind === "invite" ? "invite_sent" : "reset_sent").replace("{email}", u.email)); return; }
      let copied = false;
      try { await navigator.clipboard.writeText(r.data.link); copied = true; } catch (e) { /* ignore */ }
      toast.warning(t(r.data.email_configured ? "link_send_failed" : "link_no_email") + (copied ? " " + t("link_copied") : " " + r.data.link), { duration: 15000 });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (u) => {
    if (u.id === user.id) return toast.error(t("cant_delete_self"));
    if (!window.confirm(t("delete_user_confirm").replace("{{name}}", u.name))) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => [u.name, u.email, u.employee_id, u.department, u.phone, u.campus]
        .some((v) => (v || "").toLowerCase().includes(q)))
    : users;

  const canEdit = (u) => !limited || ["staff", "ict_admin"].includes(u.role);
  const allNames = users.map((u) => u.name);
  const allDepts = [...new Set(users.map((u) => u.department).filter(Boolean))];

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">{t("access_control")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("team_users")}
          </h1>
        </div>
        <div className="flex items-center gap-3">
        {!limited && <Button variant="outline" className="rounded-none" onClick={() => setBulkOpen(true)} data-testid="bulk-add-user-button">
          <Upload className="w-4 h-4 mr-2" strokeWidth={1.75} /> {t("bulk_add_users")}
        </Button>}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} /> {t("add_user")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{t("create_user")}</DialogTitle></DialogHeader>
            <form onSubmit={submit}>
              <FormFields values={form} set={setForm} isEdit={false} t={t} allNames={allNames} allDepts={allDepts} users={users} canSuper={isSuperAdmin(user?.role)} limited={limited} fixedCampus={ownCampus} />
              <DialogFooter className="mt-6">
                <Button type="submit" className="rounded-none bg-slate-900" data-testid="submit-user-button">
                  {t("create_user")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {bulkOpen && <BulkUsersDialog onClose={() => setBulkOpen(false)} onImported={load} />}

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("us_search")}
          className="rounded-none pl-9 pr-9 bg-white"
          autoComplete="off"
          data-testid="users-search-input"
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left label-mono py-3 px-4">{t("col_employee_id")}</th>
                <th className="text-left label-mono py-3 px-4">{t("col_name")}</th>
                <th className="text-left label-mono py-3 px-4">{t("email")}</th>
                <th className="text-left label-mono py-3 px-4">{t("role")}</th>
                <th className="text-left label-mono py-3 px-4">{t("department")}</th>
                <th className="text-left label-mono py-3 px-4">{t("col_campus")}</th>
                <th className="text-left label-mono py-3 px-4">{t("col_joined")}</th>
                <th className="text-right label-mono py-3 px-4">{t("col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-slate-500 py-12">{t("loading")}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-slate-500 py-12">{t("us_no_match")}</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 text-slate-600 font-mono text-xs">{u.employee_id || "—"}</td>
                  <td className="py-3 px-4 font-medium text-slate-900">{u.name}</td>
                  <td className="py-3 px-4 text-slate-700">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 border capitalize ${
                      isSuperAdmin(u.role) ? "bg-amber-50 text-amber-800 border-amber-300 font-medium"
                        : isAdminRole(u.role) ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"
                    }`} data-testid={`role-badge-${u.id}`}>{isSuperAdmin(u.role) ? t("super_admin") : isAdminRole(u.role) ? t("admin") : ["ict_admin", "campus_admin", "leader"].includes(u.role) ? t(u.role) : t("staff")}</span>
                  </td>
                  <td className="py-3 px-4 text-slate-700">{u.department || "—"}</td>
                  <td className="py-3 px-4 text-slate-700">
                    {u.campus ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.campus === "YPJ Kuala Kencana" ? "bg-amber-400" : "bg-blue-500"}`} />
                        <span className="text-xs">{campusLabel(u.campus)}</span>
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-3 px-4 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit(u) && <>
                      <button onClick={() => sendLink(u, "invite")} title={t("send_invitation")} className="text-slate-400 hover:text-blue-700" data-testid={`invite-user-${u.id}`}>
                        <Mail className="w-4 h-4" />
                      </button>
                      <button onClick={() => sendLink(u, "reset")} title={t("reset_password")} className="text-slate-400 hover:text-amber-600" data-testid={`reset-user-${u.id}`}>
                        <KeyRound className="w-4 h-4" />
                      </button>
                      </>}
                      {(!isSuperAdmin(u.role) || isSuperAdmin(user?.role)) && canEdit(u) && (
                      <button onClick={() => openEdit(u)} className="text-slate-400 hover:text-slate-700" data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>)}
                      {u.id !== user?.id && (!isSuperAdmin(u.role) || isSuperAdmin(user?.role)) && canEdit(u) && (
                        <button onClick={() => remove(u)} className="text-rose-500 hover:text-rose-700" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit user dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("edit_user")}</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit}>
            <FormFields values={editForm} set={setEditForm} isEdit={true} t={t} allNames={allNames} allDepts={allDepts} users={users} canSuper={isSuperAdmin(user?.role)} limited={limited} fixedCampus={ownCampus} />
            <DialogFooter className="mt-6">
              <Button variant="outline" className="rounded-none" type="button" onClick={() => setEditOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" className="rounded-none bg-slate-900">{t("save_changes")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
